import { dayjs, TASHKENT_ZONE } from '@noor/lib';

/**
 * Slot time-field derivation — cpanel's `buildBonusTimeFields`. Produces the
 * four ISO fields the API expects from the form's `HH:MM:SS` clocks and the
 * from/to calendar days:
 *
 *   from_time   = (1970-01-01 @ start_time, UTC) − 5h        [clock time only]
 *   till_time   = (1970-01-01 @ end_time,   UTC) − 5h
 *   active_from = (`from` day @ active_from_time, Tashkent) → ISO
 *   active_till = (`to`   day @ active_till_time, Tashkent) → ISO
 *
 * The 1970-01-01 anchor of `from_time` / `till_time` is intentional: only the
 * clock time round-trips, the date part is discarded.
 */
export interface SlotTimeInput {
  /** `HH:MM:SS`; empty reads as midnight (cpanel's fallback). */
  start_time?: string | undefined;
  end_time?: string | undefined;
  active_from_time?: string | undefined;
  active_till_time?: string | undefined;
  /** Calendar days (`YYYY-MM-DD`, or any ISO instant — only its date is used). */
  from?: string | null | undefined;
  to?: string | null | undefined;
}

export interface SlotTimeFields {
  active_from: string;
  active_till: string;
  from_time: string;
  till_time: string;
}

const MIDNIGHT = '00:00:00';
const DAY_FORMAT = 'YYYY-MM-DD';
/** Tashkent is UTC+5 all year (no DST). */
const TASHKENT_OFFSET_HOURS = 5;

export function buildSlotTimeFields(input: SlotTimeInput): SlotTimeFields {
  const fromClock = dayjs
    .utc(`1970-01-01T${input.start_time ?? MIDNIGHT}`)
    .subtract(TASHKENT_OFFSET_HOURS, 'hour');
  const tillClock = dayjs
    .utc(`1970-01-01T${input.end_time ?? MIDNIGHT}`)
    .subtract(TASHKENT_OFFSET_HOURS, 'hour');
  const fromDay = dayjs(input.from).format(DAY_FORMAT);
  const toDay = dayjs(input.to).format(DAY_FORMAT);
  return {
    active_from: dayjs
      .tz(`${fromDay}T${input.active_from_time ?? MIDNIGHT}`, TASHKENT_ZONE)
      .toISOString(),
    active_till: dayjs
      .tz(`${toDay}T${input.active_till_time ?? MIDNIGHT}`, TASHKENT_ZONE)
      .toISOString(),
    from_time: fromClock.toISOString(),
    till_time: tillClock.toISOString(),
  };
}

/**
 * A stored instant shifted to Tashkent wall time by the FIXED +5h offset
 * (cpanel `utcToGmt`). Deliberately not zone-database based: `from_time` /
 * `till_time` are anchored at 1970-01-01, when Tashkent was UTC+6, and the
 * anchor is built with the same fixed −5h (see `buildSlotTimeFields`) — so the
 * clock only round-trips with the fixed offset.
 */
function tashkentWall(value: string | null | undefined) {
  if (!value) return undefined;
  const wall = dayjs.utc(value).add(TASHKENT_OFFSET_HOURS, 'hour');
  return wall.isValid() ? wall : undefined;
}

/** The Tashkent wall clock (`HH:mm:ss`) of a stored instant, or `undefined`. */
export function toTashkentClock(value: string | null | undefined): string | undefined {
  return tashkentWall(value)?.format('HH:mm:ss');
}

/** The Tashkent calendar day (`YYYY-MM-DD`) of a stored instant, or `undefined`. */
export function toTashkentDay(value: string | null | undefined): string | undefined {
  return tashkentWall(value)?.format(DAY_FORMAT);
}
